import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, Mail, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PLACEHOLDERS = [
  { tag: "{bedrift}", desc: "Bedriftens navn" },
  { tag: "{navn}", desc: "Ditt navn" },
  { tag: "{firma}", desc: "Firmanavn" },
  { tag: "{telefon}", desc: "Telefon" },
  { tag: "{epost}", desc: "E-post" },
];

const fill = (t, s, company) =>
  (t || "")
    .replaceAll("{bedrift}", company)
    .replaceAll("{navn}", s.sender_name)
    .replaceAll("{firma}", s.sender_company)
    .replaceAll("{telefon}", s.sender_phone)
    .replaceAll("{epost}", s.sender_email);

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.getSettings().then(setS);
  }, []);

  const upd = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.updateSettings({
        sender_name: s.sender_name,
        sender_company: s.sender_company,
        sender_phone: s.sender_phone,
        subject_template: s.subject_template,
        body_template: s.body_template,
      });
      setS(saved);
      toast.success("Innstillinger lagret");
    } catch (e) {
      toast.error("Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  const applyToLeads = async () => {
    setApplying(true);
    try {
      const res = await api.applySettings();
      toast.success(`Oppdaterte ${res.updated} utkast (ikke-redigerte, ikke-sendte)`);
    } catch (e) {
      toast.error("Kunne ikke oppdatere utkast");
    } finally {
      setApplying(false);
    }
  };

  if (!s) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const previewCompany = "Heggedal Frisør";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-card/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
          <Link to="/pro/prospekter" data-testid="back-to-prospects">
            <Button size="icon" variant="outline"><ArrowLeft className="h-4 w-4" /></Button>
          </Link>
          <div>
            <h1 className="font-heading text-lg font-extrabold tracking-tight leading-none">Innstillinger</h1>
            <p className="text-xs text-muted-foreground">E-postmal og signatur</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-5">
          <section className="rounded-2xl border border-border bg-card p-5 space-y-4">
            <h2 className="font-heading text-base font-bold">Avsender / signatur</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Navn</Label>
                <Input data-testid="settings-name" value={s.sender_name} onChange={(e) => upd("sender_name", e.target.value)} className="bg-background" />
              </div>
              <div>
                <Label className="text-xs">Telefon</Label>
                <Input data-testid="settings-phone" value={s.sender_phone} onChange={(e) => upd("sender_phone", e.target.value)} className="bg-background" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Firma</Label>
                <Input data-testid="settings-company" value={s.sender_company} onChange={(e) => upd("sender_company", e.target.value)} className="bg-background" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">E-post (fast avsender)</Label>
                <Input value={s.sender_email} disabled className="bg-secondary" />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 space-y-4">
            <h2 className="font-heading text-base font-bold">E-postmal</h2>
            <div>
              <Label className="text-xs">Emne</Label>
              <Input data-testid="settings-subject" value={s.subject_template} onChange={(e) => upd("subject_template", e.target.value)} className="bg-background" />
            </div>
            <div>
              <Label className="text-xs">Meldingstekst</Label>
              <Textarea data-testid="settings-body" value={s.body_template} onChange={(e) => upd("body_template", e.target.value)} rows={14} className="bg-background text-sm leading-relaxed resize-none scroll-thin" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PLACEHOLDERS.map((p) => (
                <span key={p.tag} className="rounded-md border border-border bg-secondary px-2 py-0.5 text-[11px] font-mono-brand" title={p.desc}>
                  {p.tag}
                </span>
              ))}
            </div>
          </section>

          <div className="flex flex-wrap gap-2">
            <Button data-testid="settings-save-button" onClick={save} disabled={saving} className="gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre
            </Button>
            <Button data-testid="settings-apply-button" variant="outline" onClick={applyToLeads} disabled={applying} className="gap-1.5">
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Bruk på eksisterende utkast
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            «Bruk på eksisterende utkast» oppdaterer alle e-poster som ikke er redigert manuelt og ikke er sendt.
          </p>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-20 h-fit">
          <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-2 text-xs font-mono-brand uppercase tracking-wider text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Forhåndsvisning ({previewCompany})
            </div>
            <div className="rounded-xl border border-border bg-secondary/50 p-4 space-y-2">
              <div className="text-sm"><span className="text-muted-foreground">Til:</span> post@heggedalfrisor.no</div>
              <div className="text-sm font-semibold flex items-start gap-1.5">
                <Mail className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                <span data-testid="preview-subject">{fill(s.subject_template, s, previewCompany)}</span>
              </div>
              <pre data-testid="preview-body" className="whitespace-pre-wrap font-sans text-sm text-foreground/90 leading-relaxed border-t border-border pt-2">
{fill(s.body_template, s, previewCompany)}
              </pre>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
